import * as XLSX from 'xlsx';

// Helper function to get backend data URL
const getDataUrl = (path) => {
  const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';
  const url = `${API_BASE}/data/${path}`;
  console.log('🔗 Building data URL:', url);
  return url;
};

// Configuration constants
const CONFIG = {
  BACKROOM_SQ_FT: 109.5,
  COST_PER_DELIVERY: 634,
  LEAD_TIME_DAYS: 1,
  STORAGE_DENSITY: 10, // units per sq ft (average assumption)
  SIMULATION_DAYS: 90
};

export async function loadASHSalesData() {
  try {
    console.log('=== LOADING REAL ASH SALES DATA ===');
    const dataUrl = getDataUrl('inventory/ASH.xlsx');
    console.log('Fetching from:', dataUrl);
    
    const response = await fetch(dataUrl);
    console.log('Response status:', response.status, 'OK:', response.ok);
    if (!response.ok) {
      throw new Error(`Failed to fetch ASH file: ${response.status} ${response.statusText}`);
    }
    
    const arrayBuffer = await response.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    
    // Convert sheet to array of arrays (not objects) to access by index
    const rawData = XLSX.utils.sheet_to_json(sheet, { header: 1 }); // header: 1 gives arrays
    
    console.log('✅ Raw ASH data loaded:', rawData.length, 'rows');
    console.log('📄 First row (headers):', rawData[0]);
    console.log('📄 Second row (data):', rawData[1]);
    console.log('📄 Column C (index 2):', rawData[1] ? rawData[1][2] : 'N/A');
    console.log('📄 Column D (index 3):', rawData[1] ? rawData[1][3] : 'N/A');
    console.log('📄 Column M (index 12):', rawData[1] ? rawData[1][12] : 'N/A');
    
    // Skip header row (index 0), start from row 1
    // Map to our format using direct column indices:
    // Column C = index 2 (SKU), Column D = index 3 (Description), Column M = index 12 (Units Sold)
    const salesData = rawData
      .slice(1) // Skip header row
      .map((row, idx) => {
        const sku = String(row[2] || '').trim();           // Column C = index 2
        const description = String(row[3] || '').trim();    // Column D = index 3  
        const unitsSold = parseFloat(row[12] || 0);         // Column M = index 12
        
        if (idx < 5) {
          console.log(`Data Row ${idx}: SKU="${sku}", Desc="${description}", Units=${unitsSold}`);
          console.log(`Raw array row ${idx}:`, row);
        }
        
        return { 
          sku, 
          description, 
          annualSales: unitsSold,  // Units sold = annual sales
          category: 'GENERAL'  // Default category
        };
      })
      .filter(item => item.sku && item.annualSales > 0);
    
    console.log('✅ Valid SKUs extracted:', salesData.length);
    
    if (salesData.length === 0) {
      console.error('❌ NO VALID SKUs FOUND - Check column mapping');
      console.error('Expected columns: SKU (C), Generic/Single Description (D), Units Sold (M)');
      console.error('Available columns:', Object.keys(rawData[0] || {}));
      return [];
    }
    
    console.log('🔥 Top 5 ASH SKUs by sales volume:');
    salesData
      .sort((a, b) => b.annualSales - a.annualSales)
      .slice(0, 5)
      .forEach((item, i) => {
        console.log(`  ${i+1}. SKU: ${item.sku} | ${item.description} | Sales: ${item.annualSales.toLocaleString()}`);
      });
    
    return salesData;
    
  } catch (error) {
    console.error('❌ ERROR loading ASH sales data:', error);
    console.error('Returning empty array - NO fallback data');
    return [];
  }
}

export async function loadASHPlanogramData() {
  try {
    console.log('=== LOADING REAL ASH PLANOGRAM DATA ===');
    
    const dataUrl = getDataUrl('Asheville MIN MAX Master.xlsx');
    console.log('Fetching Asheville MIN MAX from:', dataUrl);
    const response = await fetch(dataUrl);
    console.log('Response status:', response.status, 'OK:', response.ok);
    if (!response.ok) {
      throw new Error('Asheville planogram file not found at /data/Asheville MIN MAX Master.xlsx');
    }
    
    const arrayBuffer = await response.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
    const planogramData = XLSX.utils.sheet_to_json(firstSheet);
    
    console.log('Raw ASH planogram data loaded:', planogramData.length, 'records');
    console.log('Sample planogram record:', planogramData[0]);
    console.log('Available columns:', Object.keys(planogramData[0] || {}));
    
    // Create lookup map using correct column names from actual file
    const planogramLookup = {};
    
    planogramData.forEach((row, idx) => {
      // Column B = 'Article' (the SKU - this is the correct column name!)
      const sku = String(row['Article'] || '').trim();
      
      // Column F = 'Planogram  MAX ' (with extra spaces - this is the exact name!)
      const planogramMax = parseFloat(
        row['Planogram  MAX '] ||  // Note the extra spaces!
        row['Planogram MAX'] ||     // Try without extra space too
        row['Planogram_MAX'] ||
        0
      );
      
      if (idx < 5) {
        console.log(`Planogram row ${idx}: SKU="${sku}", planogramMax=${planogramMax}`);
        console.log(`Raw planogram row ${idx}:`, row);
      }
      
      if (sku && planogramMax > 0) {
        planogramLookup[sku] = { planogramMax };
      }
    });
    
    console.log('✅ Planogram lookup created for', Object.keys(planogramLookup).length, 'SKUs');
    console.log('🔥 Sample SKUs with planogram values:', Object.entries(planogramLookup).slice(0, 5));
    
    return planogramLookup;
    
  } catch (error) {
    console.error('Error loading planogram data:', error);
    return {};
  }
}

export function optimizeDeliverySchedule(salesData, planogramData, config = CONFIG) {
  console.log('=== ASHEVILLE DELIVERY OPTIMIZATION STARTING ===');
  console.log('Using config:', config);
  
  // Step 1: Filter to only SKUs with planogram values
  const validSkus = salesData
    .filter(item => planogramData[item.sku]?.planogramMax > 0)
    .map(item => ({
      sku: item.sku,
      description: item.description,
      dailySales: item.annualSales / 365,
      planogramMax: planogramData[item.sku].planogramMax,
      category: item.category,
      salesPerDay: (item.annualSales / 365)
    }))
    .sort((a, b) => b.salesPerDay - a.salesPerDay);

  console.log('Valid SKUs for simulation:', validSkus.length);
  console.log('Top 10 velocity SKUs:');
  validSkus.slice(0, 10).forEach((sku, i) => {
    console.log(`  ${i+1}. ${sku.sku}: ${sku.salesPerDay.toFixed(2)} sales/day, planogram: ${sku.planogramMax}`);
  });

  // Step 2: Test delivery scenarios
  const scenarios = [
    { name: 'Daily', pattern: [1,1,1,1,1,1,1], maxGapDays: 1 },
    { name: 'Mon/Wed/Fri', pattern: [0,1,0,1,0,1,0], maxGapDays: 2 },
    { name: 'Mon/Thu', pattern: [0,1,0,0,1,0,0], maxGapDays: 3 },
    { name: 'Monday Only', pattern: [0,1,0,0,0,0,0], maxGapDays: 7 }
  ];

  const results = scenarios.map(scenario => 
    simulateScenario(validSkus, scenario, config)
  );

  console.log('=== SIMULATION RESULTS ===');
  results.forEach(result => {
    console.log(`${result.scenario}: ${result.inStockRate} in-stock, $${result.totalCost} total cost`);
  });

  return {
    validSkus: validSkus.length,
    scenarios: results,
    topVelocitySkus: validSkus.slice(0, 20) // Top 20 for detailed display
  };
}

function simulateScenario(skus, scenario, config = CONFIG) {
  console.log(`\n--- Simulating ${scenario.name} scenario ---`);
  console.log('Using config for scenario:', config.BACKROOM_SQ_FT, 'sq ft backroom');
  
  // Step 1: Calculate MIN/MAX for each SKU based on delivery frequency
  const skuConfigs = skus.map(sku => {
    // Safety stock = cover max gap between deliveries + lead time
    const safetyDays = scenario.maxGapDays + config.LEAD_TIME_DAYS;
    const safetyStock = sku.dailySales * safetyDays;
    
    // MIN = reorder point (safety stock level)
    const min = Math.ceil(safetyStock);
    
    // MAX = planogram + backroom allocation (TBD)
    let max = sku.planogramMax;
    
    // Excess needed beyond planogram to hold safety stock
    const excessNeeded = Math.max(0, min - sku.planogramMax);
    
    return {
      ...sku,
      min: min,
      max: max, // will adjust after backroom allocation
      excessNeeded: excessNeeded,
      needsBackroom: excessNeeded > 0
    };
  });

  // Step 2: Allocate backroom space to high-velocity items that need it
  let backroomRemaining = config.BACKROOM_SQ_FT;
  let skusNeedingBackroom = skuConfigs.filter(sku => sku.needsBackroom);
  
  console.log(`${skusNeedingBackroom.length} SKUs need backroom space`);
  
  skuConfigs.forEach(sku => {
    if (sku.needsBackroom && backroomRemaining > 0) {
      const sqFtNeeded = sku.excessNeeded / config.STORAGE_DENSITY;
      const sqFtAllocated = Math.min(sqFtNeeded, backroomRemaining);
      const unitsAllocated = Math.floor(sqFtAllocated * config.STORAGE_DENSITY);
      
      sku.backroomAllocation = unitsAllocated;
      sku.max = sku.planogramMax + unitsAllocated;
      backroomRemaining -= sqFtAllocated;
    } else {
      sku.backroomAllocation = 0;
    }
  });

  // Step 3: Simulate inventory over time
  const inventory = skuConfigs.map(sku => ({
    ...sku,
    onHand: sku.max,
    pendingOrder: 0,
    stockoutUnits: 0,
    stockoutDays: 0
  }));

  let deliveryCount = 0;

  for (let day = 0; day < config.SIMULATION_DAYS; day++) {
    const dayOfWeek = day % 7;
    const isDeliveryDay = scenario.pattern[dayOfWeek] === 1;

    // Process each SKU
    inventory.forEach(item => {
      // Receive pending orders (arrives day after order)
      if (item.pendingOrder > 0 && day > 0) {
        item.onHand += item.pendingOrder;
        item.onHand = Math.min(item.onHand, item.max); // Don't exceed MAX
        item.pendingOrder = 0;
      }

      // Daily sales
      const demandToday = item.dailySales;
      const sold = Math.min(demandToday, item.onHand);
      item.onHand -= sold;
      
      // Track stockouts
      if (sold < demandToday) {
        item.stockoutUnits += (demandToday - sold);
        item.stockoutDays++;
      }

      // Check if we should reorder (hit MIN and it's delivery day)
      if (item.onHand <= item.min && isDeliveryDay && item.pendingOrder === 0) {
        // Place order to bring to MAX
        const orderQty = item.max - item.onHand;
        item.pendingOrder = orderQty;
      }
    });

    // Count deliveries
    if (isDeliveryDay) {
      deliveryCount++;
    }
  }

  // Calculate final metrics
  const totalPossibleSales = inventory.reduce((sum, item) => 
    sum + (item.dailySales * config.SIMULATION_DAYS), 0);
  const totalStockouts = inventory.reduce((sum, item) => sum + item.stockoutUnits, 0);
  const inStockRate = ((totalPossibleSales - totalStockouts) / totalPossibleSales) * 100;
  
  const totalCost = deliveryCount * config.COST_PER_DELIVERY;
  const costPerWeek = Math.round(totalCost / (config.SIMULATION_DAYS / 7));
  
  const backroomUsed = config.BACKROOM_SQ_FT - backroomRemaining;

  console.log(`${scenario.name} Results:`, {
    deliveries: deliveryCount,
    totalCost: totalCost,
    inStockRate: inStockRate.toFixed(1) + '%',
    backroomUsed: backroomUsed.toFixed(1) + ' sq ft'
  });

  return {
    scenario: scenario.name,
    deliveryPattern: scenario.pattern,
    deliveriesPerWeek: scenario.pattern.reduce((a,b) => a+b, 0),
    totalDeliveries: deliveryCount,
    totalCost: totalCost,
    costPerWeek: costPerWeek,
    inStockRate: inStockRate.toFixed(1) + '%',
    totalStockouts: Math.round(totalStockouts),
    backroomUsage: backroomUsed.toFixed(1) + ' sq ft',
    skuConfigs: inventory.slice(0, 50), // Top 50 for detailed analysis
    backroomItems: inventory
      .filter(sku => sku.backroomAllocation > 0)
      .map(sku => ({
        sku: sku.sku,
        description: sku.description,
        dailySales: sku.dailySales,
        planogramMax: sku.planogramMax,
        backroomAllocation: sku.backroomAllocation,
        max: sku.max,
        sqFtUsed: sku.backroomAllocation / config.STORAGE_DENSITY
      }))
      .sort((a, b) => b.sqFtUsed - a.sqFtUsed), // Sort by space usage
    summaryStats: {
      totalSkus: inventory.length,
      skusNeedingBackroom: inventory.filter(item => item.backroomAllocation > 0).length,
      avgStockoutPerSku: (totalStockouts / inventory.length).toFixed(1)
    }
  };
}


// Data loading functions for real integration
export async function loadASHDataFiles() {
  try {
    console.log('=== LOADING REAL ASH DATA FILES ===');
    
    // Load real sales data first
    const salesData = await loadASHSalesData();
    if (salesData.length === 0) {
      throw new Error('No sales data loaded');
    }
    
    // Load real planogram data
    const planogramData = await loadASHPlanogramData();
    if (Object.keys(planogramData).length === 0) {
      throw new Error('No planogram data loaded');
    }
    
    console.log('=== DATA LOADING SUMMARY ===');
    console.log('Sales data records:', salesData.length);
    console.log('Planogram SKUs:', Object.keys(planogramData).length);
    
    // Find intersection - SKUs that have both sales and planogram data
    const intersection = salesData.filter(item => planogramData[item.sku]);
    console.log('SKUs with both sales AND planogram data:', intersection.length);
    
    if (intersection.length === 0) {
      console.error('No SKUs found with both sales and planogram data!');
      console.log('Sales SKUs sample:', salesData.slice(0, 5).map(s => s.sku));
      console.log('Planogram SKUs sample:', Object.keys(planogramData).slice(0, 5));
    }
    
    return { salesData, planogramData };
    
  } catch (error) {
    console.error('❌ Error loading ASH data files:', error);
    throw error; // Don't fall back to fake data - let the error bubble up
  }
}

// Main simulation runner
export async function runAshevilleSimulation() {
  try {
    console.log('=== STARTING ASHEVILLE SIMULATION ===');
    
    // Get config from localStorage
    const backroomConfig = JSON.parse(localStorage.getItem('backroomSpaceConfig') || '{}');
    const ashBackroomConfig = backroomConfig['ASH'] || {};
    const backroomSqFt = parseFloat(ashBackroomConfig['9000'] || CONFIG.BACKROOM_SQ_FT);
    
    console.log('📊 Using backroom space:', backroomSqFt, 'sq ft (from localStorage or default)');
    
    if (backroomSqFt <= 0) {
      console.warn('⚠️ No backroom space configured for Asheville. Using default 109.5 sq ft');
    }
    
    // Update CONFIG with actual backroom space
    const actualConfig = { ...CONFIG, BACKROOM_SQ_FT: backroomSqFt };
    
    const { salesData, planogramData } = await loadASHDataFiles();
    
    // CHECK IF DATA ACTUALLY LOADED
    if (salesData.length === 0) {
      console.error('❌ FAILED TO LOAD SALES DATA - Check file path and format');
      throw new Error('Failed to load ASH sales data. Check console for details.');
    }
    
    if (Object.keys(planogramData).length === 0) {
      console.error('❌ FAILED TO LOAD PLANOGRAM DATA - Check file path and format');
      throw new Error('Failed to load planogram data. Check console for details.');
    }
    
    console.log(`✅ Successfully loaded ${salesData.length} real SKUs from ASH.xlsx`);
    console.log(`✅ Successfully loaded ${Object.keys(planogramData).length} planogram records`);
    
    // Find intersection before running simulation
    const skusWithBothData = salesData.filter(item => planogramData[item.sku]);
    console.log(`🎯 SKUs with both sales AND planogram data: ${skusWithBothData.length}`);
    
    if (skusWithBothData.length === 0) {
      console.error('❌ NO SKUs found with both sales and planogram data');
      console.error('Sales SKU sample:', salesData.slice(0, 10).map(s => s.sku));
      console.error('Planogram SKU sample:', Object.keys(planogramData).slice(0, 10));
      throw new Error('No matching SKUs found between sales and planogram data');
    }
    
    const results = optimizeDeliverySchedule(salesData, planogramData, actualConfig);
    
    console.log('=== SIMULATION COMPLETED ===');
    console.log('🎯 REAL DATA SIMULATION RESULTS:');
    results.scenarios.forEach(scenario => {
      console.log(`  ${scenario.scenario}: ${scenario.inStockRate} in-stock, $${scenario.totalCost.toLocaleString()} total cost`);
    });
    
    return results;
    
  } catch (error) {
    console.error('❌ Simulation failed:', error);
    throw error;
  }
}

// Export configuration for reference
export const SIMULATION_CONFIG = CONFIG;